import React from 'react';
import { Fiche, useReadonlyState } from '@mairistem/fiche';
import { toast } from 'react-toastify';
import {
  FichePanelComplements,
  Fichiers,
  getErrorMessage,
  FilterScope,
  Icon,
  PANEL_TYPE,
  TYPE_GABARIT,
  useGabaritPj,
  SemaphoreModal,
} from '@mairistem/finances-utils';
import {
  LigneMouvement,
  Mouvement,
  MULTI,
  PieceJointeBar,
  Sens,
  Tiers as TiersInterface,
} from '@mairistem/comptabilite';
import { isNewIdentifiant, newIdentifiant } from '@mairistem/tools';
import { useLocation } from 'react-router';
import {
  keys, omit, isEmpty, isNil, forOwn, pickBy,
  isEqual,
} from 'lodash';
import dayjs from 'dayjs';
import ProfilEcritureFactory from '../../../classes/mouvements/profilEcriture/ProfilEcritureFactory';
import InformationsGenerales from '../FichePanel/InformationsGenerales/InformationsGenerales';
import Tiers from '../FichePanel/Tiers/Tiers';
import LignesPieces from '../FichePanel/LignesPieces/LignesPieces';
import PanelData from '../../../interfaces/pieces/mouvements/panelData';
import PanelOptions from '../../../interfaces/pieces/mouvements/panelOptions';
import DefaultAttributes from '../../../interfaces/pieces/mouvements/defaultAttributes';
import BaseProfilEcritureMouvement from '../../../classes/mouvements/profilEcriture/Base/BaseProfilEcritureMouvement';
import CODE_PANEL from '../../../constants/mouvements/codePanel';
import MouvementPanel from '../../../classes/mouvements/panel/MouvementPanel';
import {
  GROUPE_BASIQUE,
  GroupeProfilEcriture,
  GROUPES_PROFIL_ECRITURE,
} from '../../../constants/mouvements/groupesProfilEcriture';
import getLibelleTiersBySens from '../../../utils/mouvements/tiers';
import { getRegies } from '../../../services/piecesComptables/regies/regies';
import { getServicesDebit, createServiceDebit } from '../../../services/piecesComptables/servicesDebit';
import getCodesProduitsLocaux from '../../../services/parametres/codeProduitLocal';
import getComptesTiers from '../../../services/tiers/compte';
import { getTravauxRegie } from '../../../services/piecesComptables/travauxRegie';
import EditionAsap from '../../../constants/mouvements/editionAsap';
import CODE_BHM from '../../../constants/mouvements/codeBHM';
import { getExercice } from '../../../services/entites/exercices';
import StorageKey from '../../../constants/common/storageKey';
import { getDefaultDelaiPaiement, onChangeDuree } from '../FichePanel/Complements/DelaiPaiement/delaiPaiementUtils';
import './Mouvement.less';

interface MouvementFicheConsumerProps {
  handleChangeModalSemaphoreVisible: React.Dispatch<React.SetStateAction<boolean>>;
  identifiant: number | string;
  isModalSemaphoreVisible: boolean;
  onChange: (data: Partial<Mouvement>) => void;
  piece: Mouvement;
  selectedProfilEcriture: BaseProfilEcritureMouvement;
  setSelectedProfilEcriture: React.Dispatch<React.SetStateAction<BaseProfilEcritureMouvement>>;
}

const noResetProperties = [
  'bordereau',
  'codeBHM',
  'defaultCodeProfilEcriture',
  'exercice',
  'identifiant',
  'identifiantBordereau',
  'identifiantEntite',
  'identifiantExercice',
  'objetPiece',
  'pieceJointe',
  'sens',
];

const MouvementFicheConsumer = ({
  handleChangeModalSemaphoreVisible,
  identifiant,
  isModalSemaphoreVisible,
  onChange,
  piece,
  selectedProfilEcriture,
  setSelectedProfilEcriture,
  ...rest
}: MouvementFicheConsumerProps) => {
  const [defaultAttributes, setDefaultAttributes] = React.useState<DefaultAttributes>({});
  const [selectedTiers, setSelectedTiers] = React.useState<TiersInterface>();
  const [panelData, setPanelData] = React.useState<PanelData>({});
  const [panelOptions, setPanelOptions] = React.useState<PanelOptions>({});
  const isDirty = React.useRef(false);
  const [isTiersMulti, setIsTiersMulti] = React.useState<boolean>(false);
  const [loadedPanelCodes, setLoadedPanelCodes] = React.useState<string[]>([]);
  const [groupeProfilEcriture, setGroupeProfilEcriture] = React.useState<GroupeProfilEcriture>();
  const { search } = useLocation();
  const gabaritTypes = React.useMemo(() => [TYPE_GABARIT.OPO], []);
  const [, gabaritsPj] = useGabaritPj(gabaritTypes);
  const [readonly] = useReadonlyState();

  const visiblePanelsRef = React.useRef<Set<string>>(new Set());

  const isPanelOpen = React.useCallback((code: string) => visiblePanelsRef.current.has(code), []);

  const {
    lignes,
    sens,
    tiers,
    codeProfilEcriture,
    // @ts-expect-error
    deletedLignes,
  } = piece || {};

  const handleDefaultAttributes = (attributes: object) => setDefaultAttributes((old) => ({
    ...old,
    ...attributes,
  }));

  const handleSelectAsapEdition = React.useCallback((isTiersMultiple: boolean) => {
    if (selectedProfilEcriture?.panels?.find(
      (panel) => (panel?.code === CODE_PANEL.EDITION_ASAP_DEMATERIALISEE && panel.isVisible(piece)),
    )) {
      const changes = selectedProfilEcriture.getAsapEditionDefaultAttributes(
        isTiersMultiple,
        piece,
        true,
      );

      if (changes) {
        onChange(changes);
      }
    }
  }, [
    onChange,
    piece,
    selectedProfilEcriture,
  ]);

  const handleChangesMouvement = React.useCallback((changes: Partial<Mouvement>) => {
    onChange(changes);

    // Permet de savoir si des modifications ont eu lieu pour faire apparaitre la popup lors du changement de profil d'écriture
    if (keys(omit(changes, noResetProperties)).length > 0) {
      isDirty.current = true;
    }
  }, [onChange]);

  const handleChangeLignes = React.useCallback((changes: Partial<LigneMouvement>) => {
    if (!isEmpty(lignes)) {
      handleChangesMouvement({
        lignes: lignes.map((ligne) => ({
          ...ligne,
          ...changes,
        })),
      });
    }
  }, [handleChangesMouvement, lignes]);

  /**
   * Permet d'actualiser la valeur des champs dans les blocs situés sur le mouvement
   */
  React.useEffect(() => {
    if (!isEmpty(lignes)) {
      let actif = lignes[0]?.actif;
      let covid19 = lignes[0]?.covid19;
      let exerRattachement = lignes[0]?.exerRattachement;
      let fctva = lignes[0]?.fctva;
      let identifiantRegie = lignes[0]?.identifiantRegie;
      let idLigneOriginePes = lignes[0]?.idLigneOriginePes;
      let modeReglement = lignes[0]?.modeReglement;
      let naturePieceOrigine = lignes[0]?.naturePieceOrigine;
      let pieceOrigine = lignes[0]?.pieceOrigine;
      let identifiantCodprodloc = lignes[0]?.identifiantCodprodloc;
      let cocheTravauxRegie = lignes[0]?.cocheTravauxRegie;
      let identifiantTravauxRegie = lignes[0]?.identifiantTravauxRegie;
      let factureChorus = lignes[0]?.factureChorus;
      let emprunt = lignes[0]?.emprunt;
      let identifiantCompteTiers = lignes[0]?.identifiantCompteTiers;
      let identifiantTiers = lignes[0]?.identifiantTiers;
      let lfacDatefin = lignes[0]?.lfacDatefin;
      let lfacDate = lignes[0]?.lfacDate;
      let lfacQuantite = lignes[0]?.lfacQuantite;
      let lfacLibelle = lignes[0]?.lfacLibelle;
      let lfacMtunitaire = lignes[0]?.lfacMtunitaire;
      let lfacUnite1 = lignes[0]?.lfacUnite1;
      let lfacMtbase = lignes[0]?.lfacMtbase;
      let isModeAvance = !isNil(lfacMtunitaire) || !isNil(lfacQuantite) || !isNil(lfacUnite1);
      let dateDebutCharge = lignes[0]?.dateDebutCharge;
      let dateFinCharge = lignes[0]?.dateFinCharge;
      let codeArticleNonBudgetaire = lignes[0]?.codeArticleNonBudgetaire;
      let iban = lignes[0]?.iban;
      const cleRib = lignes[0]?.cleRib;
      const numeroCompte = lignes[0]?.numeroCompte;
      const codeBanque = lignes[0]?.codeBanque;
      const codeGuichet = lignes[0]?.codeGuichet;
      const codeBic = lignes[0]?.codeBic;
      let ligneTiers = lignes[0]?.tiers;
      let libelleVirementZoneLibre = lignes[0]?.libelleVirementZoneLibre;
      let libelleVirementZonLibre = lignes[0]?.libelleVirementZonLibre;

      lignes?.forEach((ligne) => {
        if (actif !== MULTI && actif !== ligne?.actif && !isEmpty(ligne?.actif)) {
          actif = MULTI;
        }
        if (covid19 !== ligne?.covid19 && !isNil(ligne?.covid19)) {
          covid19 = null;
        }
        if (exerRattachement !== MULTI
          && exerRattachement !== ligne?.exerRattachement
          && !isEmpty(ligne?.exerRattachement)
        ) {
          exerRattachement = MULTI;
        }
        if (fctva !== ligne?.fctva && !isNil(ligne?.fctva)) {
          fctva = null;
        }
        if (identifiantRegie !== MULTI
          && identifiantRegie !== ligne?.identifiantRegie
          && !isNil(ligne?.identifiantRegie)
        ) {
          identifiantRegie = MULTI;
        }
        // @ts-expect-error A modifier dans comptaLib
        if (idLigneOriginePes !== MULTI && idLigneOriginePes !== ligne?.idLigneOriginePes && !isEmpty(ligne?.idLigneOriginePes)) {
          // @ts-expect-error A modifier dans comptaLib
          idLigneOriginePes = MULTI;
        }
        if (modeReglement !== MULTI && modeReglement !== ligne?.modeReglement && !isEmpty(ligne?.modeReglement)) {
          modeReglement = MULTI;
        }
        if (naturePieceOrigine !== MULTI
          && naturePieceOrigine !== ligne?.naturePieceOrigine
          && !isEmpty(ligne?.naturePieceOrigine)
        ) {
          naturePieceOrigine = MULTI;
        }
        if (pieceOrigine !== MULTI && pieceOrigine !== ligne?.pieceOrigine && !isEmpty(ligne?.pieceOrigine)) {
          pieceOrigine = MULTI;
        }
        if (identifiantCodprodloc !== MULTI
          && identifiantCodprodloc !== ligne?.identifiantCodprodloc
          && !isNil(ligne?.identifiantCodprodloc)
        ) {
          identifiantCodprodloc = MULTI;
        }
        if (cocheTravauxRegie !== ligne?.cocheTravauxRegie && !isNil(ligne?.cocheTravauxRegie)) {
          cocheTravauxRegie = null;
        }
        if (identifiantTravauxRegie !== MULTI
          && identifiantTravauxRegie !== ligne?.identifiantTravauxRegie
          && !isNil(ligne?.identifiantTravauxRegie)
        ) {
          identifiantTravauxRegie = MULTI;
        }
        if (factureChorus !== MULTI && factureChorus !== ligne?.factureChorus && !isEmpty(ligne?.factureChorus)) {
          factureChorus = MULTI;
        }
        if (identifiantCompteTiers !== MULTI
          && identifiantCompteTiers !== ligne?.identifiantCompteTiers
          && !isNil(ligne?.identifiantCompteTiers)) {
          identifiantCompteTiers = MULTI;
        }
        if (emprunt !== MULTI && emprunt !== ligne?.emprunt && !isEmpty(ligne?.emprunt)) {
          emprunt = MULTI;
        }
        if (lfacDatefin !== MULTI && lfacDatefin !== ligne?.lfacDatefin && !isEmpty(ligne?.lfacDatefin)) {
          lfacDatefin = MULTI;
        }
        if (lfacDate !== MULTI && lfacDate !== ligne?.lfacDate && !isEmpty(ligne?.lfacDate)) {
          lfacDate = MULTI;
        }
        if (lfacQuantite !== MULTI && lfacQuantite !== ligne?.lfacQuantite && !isNil(ligne?.lfacQuantite)) {
          lfacQuantite = MULTI;
        }
        if (!isNil(ligne?.lfacMtunitaire) || !isNil(ligne?.lfacQuantite) || !isEmpty(ligne?.lfacUnite1)) {
          isModeAvance = true;
        }
        if (lfacLibelle !== MULTI && lfacLibelle !== ligne?.lfacLibelle && !isEmpty(ligne?.lfacLibelle)) {
          lfacLibelle = MULTI;
        }
        if (lfacMtunitaire !== MULTI && lfacMtunitaire !== ligne?.lfacMtunitaire && !isNil(ligne?.lfacMtunitaire)) {
          lfacMtunitaire = MULTI;
        }
        if (lfacUnite1 !== MULTI && lfacUnite1 !== ligne?.lfacUnite1 && !isEmpty(ligne?.lfacUnite1)) {
          lfacUnite1 = MULTI;
        }
        if (lfacMtbase !== MULTI && lfacMtbase !== ligne?.lfacMtbase && !isNil(ligne?.lfacMtbase)) {
          lfacMtbase = MULTI;
        }
        if (dateDebutCharge !== MULTI
          && dateDebutCharge !== ligne?.dateDebutCharge
          && !isEmpty(ligne?.dateDebutCharge)
        ) {
          dateDebutCharge = MULTI;
        }
        if (dateFinCharge !== MULTI && dateFinCharge !== ligne?.dateFinCharge && !isEmpty(ligne?.dateFinCharge)) {
          dateFinCharge = MULTI;
        }
        if (codeArticleNonBudgetaire !== MULTI
          && codeArticleNonBudgetaire !== ligne?.codeArticleNonBudgetaire
          && !isEmpty(ligne?.codeArticleNonBudgetaire)
        ) {
          codeArticleNonBudgetaire = MULTI;
        }
        if (iban !== MULTI && iban !== ligne?.iban && !isEmpty(ligne?.iban)) {
          iban = MULTI;
        }
        if (identifiantTiers !== MULTI
          && identifiantTiers !== ligne?.identifiantTiers
          && !isNil(ligne?.identifiantTiers)
        ) {
          identifiantTiers = MULTI;
          ligneTiers = {};
        }
        if (libelleVirementZoneLibre !== MULTI
          && libelleVirementZoneLibre !== ligne?.libelleVirementZoneLibre
          && !isEmpty(ligne?.libelleVirementZoneLibre)
        ) {
          libelleVirementZoneLibre = MULTI;
        }
        if (libelleVirementZonLibre !== MULTI
          && libelleVirementZonLibre !== ligne?.libelleVirementZonLibre
          && !isEmpty(ligne?.libelleVirementZonLibre)
        ) {
          libelleVirementZonLibre = MULTI;
        }
      });

      // Gestion du tiers
      const multiTiers = identifiantTiers === MULTI;
      setIsTiersMulti(multiTiers);
      // Si nous sommes sur un tiers unique et que ce n'est pas le même que celui selectionné actuellement
      if (!multiTiers && identifiantTiers !== selectedTiers?.identifiant) {
        setSelectedTiers(ligneTiers);
      } else if (multiTiers) {
        setSelectedTiers({});
      }
      handleSelectAsapEdition(multiTiers);

      /**
       * Obligé, car si on met à la main tous les champs a la même valeur sur les lignes
       * cela ne change pas la valeur par défaut
      */
      handleDefaultAttributes({
        ...((actif !== MULTI && !isEmpty(actif)) && { actif }),
        ...(!isNil(covid19) && { covid19 }),
        ...((exerRattachement !== MULTI && !isEmpty(exerRattachement)) && { exerRattachement }),
        ...(!isNil(fctva) && { fctva }),
        ...((identifiantRegie !== MULTI && !isNil(identifiantRegie)) && { identifiantRegie }),
        // @ts-expect-error A modifier dans comptaLib
        ...((idLigneOriginePes !== MULTI && !isNil(idLigneOriginePes)) && { idLigneOriginePes }),
        ...((modeReglement !== MULTI && !isEmpty(modeReglement)) && { modeReglement }),
        ...((naturePieceOrigine !== MULTI && !isEmpty(naturePieceOrigine)) && { naturePieceOrigine }),
        ...((pieceOrigine !== MULTI && !isEmpty(pieceOrigine)) && { pieceOrigine }),
        ...((identifiantCodprodloc !== MULTI && !isNil(identifiantCodprodloc))
          && { identifiantCodprodloc }),
        ...(!isNil(cocheTravauxRegie) && { cocheTravauxRegie }),
        ...((identifiantTravauxRegie !== MULTI && !isNil(identifiantTravauxRegie)) && { identifiantTravauxRegie }),
        ...((factureChorus !== MULTI && !isEmpty(factureChorus)) && { factureChorus }),
        ...((emprunt !== MULTI && !isEmpty(emprunt)) && { emprunt }),
        ...((identifiantCompteTiers !== MULTI && !isNil(identifiantCompteTiers)) && { identifiantCompteTiers }),
        ...((lfacMtbase !== MULTI && !isNil(lfacMtbase)) && { lfacMtbase }),
        ...((lfacMtunitaire !== MULTI && !isNil(lfacMtunitaire)) && { lfacMtunitaire }),
        ...((lfacQuantite !== MULTI && !isNil(lfacQuantite)) && { lfacQuantite }),
        ...((lfacUnite1 !== MULTI && !isNil(lfacUnite1)) && { lfacUnite1 }),
        ...((lfacLibelle !== MULTI && !isEmpty(lfacLibelle)) && { lfacLibelle }),
        ...((lfacDate !== MULTI && !isEmpty(lfacDate)) && { lfacDate }),
        ...((lfacDatefin !== MULTI && !isEmpty(lfacDatefin)) && { lfacDatefin }),
        ...((dateDebutCharge !== MULTI && !isEmpty(dateDebutCharge)) && { dateDebutCharge }),
        ...((dateFinCharge !== MULTI && !isEmpty(dateFinCharge)) && { dateFinCharge }),
        ...((codeArticleNonBudgetaire !== MULTI && !isEmpty(codeArticleNonBudgetaire))
          && { codeArticleNonBudgetaire }),
        isModeAvance,
        ...((identifiantTiers !== MULTI && !isNil(identifiantTiers)) && { identifiantTiers, tiers: ligneTiers }),
        ...((libelleVirementZoneLibre !== MULTI && !isEmpty(libelleVirementZoneLibre))
          && { libelleVirementZoneLibre }),
        ...((libelleVirementZonLibre !== MULTI && !isEmpty(libelleVirementZonLibre)) && { libelleVirementZonLibre }),
        ...((iban !== MULTI && !isEmpty(iban)) && {
          iban, cleRib, numeroCompte, codeBanque, codeGuichet, codeBic,
        }),
      });

      setPanelData({
        actif,
        cocheTravauxRegie,
        codeArticleNonBudgetaire,
        covid19,
        dateDebutCharge,
        dateFinCharge,
        emprunt,
        exerRattachement,
        factureChorus,
        fctva,
        iban,
        cleRib,
        codeBic,
        numeroCompte,
        codeBanque,
        codeGuichet,
        identifiantCodprodloc,
        identifiantCompteTiers,
        identifiantRegie,
        identifiantTravauxRegie,
        isModeAvance,
        lfacDate,
        lfacDatefin,
        lfacLibelle,
        lfacMtbase,
        lfacMtunitaire,
        lfacQuantite,
        lfacUnite1,
        libelleVirementZoneLibre,
        libelleVirementZonLibre,
        idLigneOriginePes,
        modeReglement,
        naturePieceOrigine,
        pieceOrigine,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lignes]);

  /**
   * Permet de supprimer les attributs par défaut sur les lignes lors de la création
   */
  React.useEffect(() => {
    if (isNewIdentifiant(identifiant)) {
      setDefaultAttributes({});
      setPanelData({});
      setPanelOptions({});
    }
  }, [identifiant]);

  /**
   * Permet d'appliquer le tiers récupéré du back
   */
  React.useEffect(() => {
    setSelectedTiers(isNewIdentifiant(identifiant) ? null : tiers);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [identifiant]);

  // Met a jour la piece si elle est créée via le speedDial
  React.useEffect(() => {
    const query = new URLSearchParams(search);
    const sensFromQuery = query.get('sens');
    if (!isEmpty(sensFromQuery) && isNewIdentifiant(piece?.identifiant)) {
      setPanelData({});
      setSelectedProfilEcriture(undefined);
      const updatePiece = async () => {
        // Récupération du paramètre sens depuis l'URL
        const identifiantExercice = Number(sessionStorage.getItem(StorageKey.IDENTIFIANT_EXERCICE));

        const newData: any = {
          ...piece,
          sens: sensFromQuery,
          codeBHM: CODE_BHM.VALUE.BROUILLARD,
          identifiantEntite: Number(sessionStorage.getItem(StorageKey.IDENTIFIANT_ENTITE)),
          identifiantExercice,
        };

        if (identifiantExercice) {
          try {
            const exercice = await getExercice(identifiantExercice);
            newData.exercice = exercice;
          } catch (e) {
            toast.error(getErrorMessage(e, "L'exercice n'a pas pu être récupéré."));
          }
        }

        // Profil d'écriture
        const code = groupeProfilEcriture?.defaultCodeProfil(sensFromQuery);
        const profil = ProfilEcritureFactory.newInstance(code, piece);
        setSelectedProfilEcriture(profil);

        onChange({
          ...newData,
          ...getDefaultDelaiPaiement(
            newData?.exercice,
            newData?.tiers,
          ),
          ...profil?.onProfilSelect?.(piece),
          codeProfilEcriture: code,
          lignes: [{
            identifiant: newIdentifiant(),
            idLigne: 1,
            ...profil?.getLigneDefaultAttributes?.(),
          }],
        });
      };

      updatePiece();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, piece?.identifiant]);

  /**
   * Permet d'ajouter automatiquement une ligne de mouvement lors de la création d'une nouvelle fiche
   * Cas où la fiche est créée sans passer par le speedDial
   */
  React.useEffect(() => {
    if (
      isNewIdentifiant(identifiant)
      && !isNil(selectedProfilEcriture)
    ) {
      onChange({
        identifiant,
        lignes: [{
          identifiant: newIdentifiant(),
          idLigne: 1,
          ...selectedProfilEcriture?.getLigneDefaultAttributes?.(),
        }],
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [identifiant, selectedProfilEcriture?.code]);

  const handleCodeProduitLocalSelect = React.useCallback(async (profil: BaseProfilEcritureMouvement) => {
    try {
      const { data } = await getCodesProduitsLocaux(
        new URLSearchParams(
          {
            all: 'true',
            type: 'CPL',
            ...(!isEmpty(profil.codesProduitLocal) && { code: FilterScope.filterIn(profil.codesProduitLocal) }),
          },
        ),
      );
      setPanelOptions((old) => ({
        ...old,
        codesProduitLocal: data.map((codeProduitLocal) => ({
          'data-testid': `codeProduitLocal${codeProduitLocal?.code}`,
          key: codeProduitLocal?.identifiant,
          text: `${codeProduitLocal?.code} - ${codeProduitLocal?.libelle}`,
          value: codeProduitLocal?.identifiant,
        })),
      }));
    } catch {
      toast.error('Impossible de récupérer les produits locaux');
    }
  }, []);

  /**
   * Au changement du profil d'écriture depuis le ProfilEcritureSearchInput
   */
  const handleChangeProfilEcriture = React.useCallback((codeProfil: string) => {
    setSelectedProfilEcriture((old) => {
      const profil = ProfilEcritureFactory.newInstance(codeProfil, piece);

      onChange({
        codeProfilEcriture: codeProfil,
        ...profil?.onProfilSelect(piece),
        // On garde le code du précédent profil d'écriture si on en avait pas déjà
        ...(isEmpty(piece?.defaultCodeProfilEcriture) && { defaultCodeProfilEcriture: old?.code }),
      });
      handleDefaultAttributes(profil?.getLigneDefaultAttributes());

      // On ne charge les codes produits locaux que si le panel est visible et que les codes sont différents d'un profil à l'autre
      if (isPanelOpen(CODE_PANEL.CODE_PRODUIT_LOCAL)
        && !isEqual(old.codesProduitLocal, profil.codesProduitLocal)) {
        handleCodeProduitLocalSelect(profil);
      }
      return profil;
    });
  }, [
    handleCodeProduitLocalSelect,
    isPanelOpen,
    onChange,
    piece,
    setSelectedProfilEcriture,
  ]);

  /**
   * Charge le profil d'écriture à l'arrivée sur la page
   */
  React.useEffect(() => {
    // Vérifie si la pièce est chargée et condition sur le profil selectionné pour ne pas le répéter.
    if (!isNil(piece?.identifiant) && isNil(selectedProfilEcriture)) {
      if (isEmpty(codeProfilEcriture)) {
        if (!isNil(groupeProfilEcriture) && !isNil(sens)) {
          const code = groupeProfilEcriture?.defaultCodeProfil(sens);
          const profil = ProfilEcritureFactory.newInstance(code, piece);
          onChange({
            ...profil?.onProfilSelect?.(piece),
            codeProfilEcriture: code,
          });
          setSelectedProfilEcriture(profil);
        }
      } else {
        // On est en modification ou création depuis une fiche existante
        const profil = ProfilEcritureFactory.newInstance(codeProfilEcriture, piece);
        setSelectedProfilEcriture(profil);
        handleDefaultAttributes(profil?.getLigneDefaultAttributes());
      }
    }
  }, [
    codeProfilEcriture,
    groupeProfilEcriture,
    onChange,
    piece,
    selectedProfilEcriture,
    sens,
    setSelectedProfilEcriture,
  ]);

  /**
   * Mise en place du groupe de profil d'écriture
   */
  React.useEffect(() => {
    // On récupère le groupe de profil d'écriture dans les queryParams
    const params = new URLSearchParams(search);
    const groupe = params.get('groupe');
    // On cherche le groupe dans notre liste
    const selectedGroupe = GROUPES_PROFIL_ECRITURE.find(
      (groupeProfil) => groupeProfil.code === groupe,
    ) ?? GROUPE_BASIQUE;
    setGroupeProfilEcriture(selectedGroupe);
  }, [search]);

  /**
   * Remise à zéro de la page lors du changement de profil d'écriture
   */
  const handleClear = React.useCallback((code: string) => {
    isDirty.current = false;
    const changes = {};

    (forOwn(piece, (v, key) => {
      if (!noResetProperties.includes(key)) {
        changes[key] = null;
      }
    }));

    onChange(changes);
    handleChangeProfilEcriture(code);
  }, [
    handleChangeProfilEcriture,
    onChange,
    piece,
  ]);

  const informationsGenerales = React.useMemo(() => (
    <Fiche.Panel key="informationsGenerales" id="informationsGenerales" title="INFORMATIONS GENERALES">
      <InformationsGenerales
        groupeProfilEcriture={groupeProfilEcriture}
        identifiant={identifiant}
        isDirty={isDirty}
        mouvement={piece}
        onChange={handleChangesMouvement}
        onChangeProfilEcriture={handleChangeProfilEcriture}
        onClear={handleClear}
        profilEcriture={selectedProfilEcriture}
      />
    </Fiche.Panel>
  ), [
    groupeProfilEcriture,
    handleChangeProfilEcriture,
    handleChangesMouvement,
    handleClear,
    identifiant,
    piece,
    selectedProfilEcriture,
  ]);

  /**
   * Applique les nouvelles données au changement du tiers
   */
  const handleChangeTiers = React.useCallback((
    ligneData: Partial<LigneMouvement>,
    mouvementData?: Pick<Mouvement, 'dureeDelaiGlobalPaiement'>,
  ) => {
    // Si nous n'avons pas de ligne, alors nous voulons quand même que les infos du Tiers soit appliquées
    if (isEmpty(lignes)) {
      if (ligneData?.modeReglement) {
        setPanelData((old) => (
          { ...old, ...ligneData }));
      }
      if (ligneData?.tiers) setSelectedTiers(ligneData.tiers);
    }
    handleChangeLignes(ligneData);
    handleDefaultAttributes(ligneData);

    if (mouvementData) {
      const newDurees = {
        ...onChangeDuree({
          dureeDelaiGlobalPaiement: mouvementData.dureeDelaiGlobalPaiement,
          dateDepartDelaiGlobalPaiement: piece.dateDepartDelaiGlobalPaiement ?? dayjs().format('YYYY-MM-DD'),
        }),
      };

      handleChangesMouvement(newDurees);
    }
  }, [handleChangeLignes, handleChangesMouvement, lignes, piece.dateDepartDelaiGlobalPaiement]);

  const tiersPanel = React.useMemo(() => (
    <Fiche.Panel id="tiers" key="tiers" title={getLibelleTiersBySens(sens)}>
      <Tiers
        exercice={piece.exercice}
        isTiersMulti={isTiersMulti}
        onChange={handleChangeTiers}
        panelData={panelData}
        profilEcriture={selectedProfilEcriture}
        sens={sens}
        tiers={selectedTiers}
      />
    </Fiche.Panel>
  ), [
    handleChangeTiers,
    isTiersMulti,
    panelData,
    piece.exercice,
    selectedProfilEcriture,
    selectedTiers,
    sens,
  ]);

  const handleRegieSelect = async () => {
    try {
      const { data } = await getRegies();
      setPanelOptions((old) => ({
        ...old,
        regies: data.map((regie) => ({
          'data-testid': `regie${regie?.code}`,
          key: regie?.identifiant,
          text: `${regie?.code} - ${regie?.libelle}`,
          value: regie?.identifiant,
        })),
      }));
    } catch {
      toast.error('Impossible de récupérer les régies');
    }
  };

  const handleTravauxEnRegieSelect = async () => {
    try {
      const { data } = await getTravauxRegie();
      setPanelOptions((old) => ({
        ...old,
        travauxEnRegie: data.map((travailEnRegie) => ({
          'data-testid': `travailEnRegie${travailEnRegie?.identifiant}`,
          key: travailEnRegie?.identifiant,
          text: travailEnRegie?.libelle,
          value: travailEnRegie?.identifiant,
        })),
      }));
    } catch {
      toast.error('Impossible de récupérer les travaux en régie');
    }
  };

  const handleCompteDeTiersSelect = async () => {
    try {
      const { data } = await getComptesTiers();
      setPanelOptions((old) => ({
        ...old,
        comptesDeTiers: data.map((compteDeTiers) => ({
          'data-testid': `compteDeTiers${compteDeTiers?.code}`,
          key: compteDeTiers?.identifiant,
          text: `${compteDeTiers?.code} - ${compteDeTiers?.libelle}`,
          value: compteDeTiers?.identifiant,
        })),
      }));
    } catch {
      toast.error('Impossible de récupérer les comptes de tiers');
    }
  };

  const handleAsapCodesServiceOptionsSelect = React.useCallback(async () => {
    if (!isNil(selectedTiers?.identifiant)) {
      try {
        const { data } = await getServicesDebit(new URLSearchParams(
          { identifiantTiers: selectedTiers?.identifiant?.toString() },
        ));

        if (!(data?.find((serviceDebit) => serviceDebit?.nom === 'FACTURE_PUBLIQUE'))) {
          const facturePubliqueCodeService = await createServiceDebit(
            {
              nom: 'FACTURE_PUBLIQUE',
              code: 'FACTURE_PUBLIQUE',
              engaOblig: false,
              defaut: isEmpty(panelOptions?.asapCodesService),
              identifiantTiers: Number(selectedTiers?.identifiant),
            },
          );
          data?.push(facturePubliqueCodeService);
        }
        setPanelOptions((old) => ({
          ...old,
          asapCodesService: data?.map((serviceDebit) => ({
            'data-testid': `codesService${serviceDebit?.code}`,
            key: serviceDebit?.identifiant,
            text: serviceDebit?.nom,
            value: serviceDebit?.code,
          })),
        }));
      } catch {
        toast.error('Impossible de récupérer les codes services ASAP');
      }
    }
  }, [panelOptions?.asapCodesService, selectedTiers?.identifiant]);

  /**
   * Permet de charger les données des panels
   * et de gérer les attributs par défaut
   * @param codes Listes des codes des panels
   */
  const handleLoadData = React.useCallback((codes: string[]) => {
    visiblePanelsRef.current = new Set(codes);
    let changesLigne: Partial<LigneMouvement> = {};
    let changesMouvement = {};

    codes?.forEach((code) => {
      switch (code) {
        case CODE_PANEL.REGIE:
          handleRegieSelect();
          break;
        case CODE_PANEL.TRAVAUX_EN_REGIE:
          handleTravauxEnRegieSelect();
          break;
        case CODE_PANEL.CODE_PRODUIT_LOCAL:
          handleCodeProduitLocalSelect(selectedProfilEcriture);
          break;
        case CODE_PANEL.COMPTE_DE_TIERS:
          handleCompteDeTiersSelect();
          break;
        case CODE_PANEL.EDITION_ASAP_DEMATERIALISEE:
          handleAsapCodesServiceOptionsSelect();
          if (!isNil(piece?.tiers)
            && !loadedPanelCodes.find((loadedCode) => loadedCode === code)
          ) {
            const attributs = selectedProfilEcriture?.getAsapEditionDefaultAttributes(isTiersMulti, piece);
            if (!isNil(attributs)) {
              changesMouvement = {
                ...changesMouvement,
                ...attributs,
              };
            }
            setLoadedPanelCodes((old) => ([...old, code]));
          }
          break;
        case CODE_PANEL.DETAIL_DE_FACTURATION:
          if (!isNil(piece)
            && !loadedPanelCodes.find((loadedCode) => loadedCode === code)
          ) {
            const attributs = null;
            if (piece.sens === Sens.RECETTE && piece.asapEdition !== null
              && Object.values(EditionAsap).includes(piece.asapEdition as EditionAsap)) {
              selectedProfilEcriture?.getDetailDeFacturationDefaultAttributes(piece);
            }

            if (!isNil(attributs)) {
              changesLigne = {
                ...changesLigne,
                ...attributs,
              };
            }
            setLoadedPanelCodes((old) => ([...old, code]));
          }
          break;
        case CODE_PANEL.PERIODE_COUVERTE:
          if (!isNil(piece?.exercice?.anneeExercice)
            && !loadedPanelCodes.find((codeLoaded) => codeLoaded === code)) {
            const attributs = selectedProfilEcriture?.getPeriodeCouverteDefaultAttributes(
              piece?.exercice?.anneeExercice,
            );
            if (!isNil(attributs)) {
              changesLigne = {
                ...changesLigne,
                ...attributs,
              };
            }
            setLoadedPanelCodes((old) => ([...old, code]));
          }
          break;
        default:
          break;
      }
    });

    if (!isEmpty(changesLigne) || !isEmpty(changesMouvement)) {
      onChange({
        ...changesMouvement,
        ...(!isEmpty(changesLigne)
          && {
            lignes: piece?.lignes?.map((ligne) => ({
              ...ligne,
              ...((!ligne?.lfacDate && changesLigne?.lfacDate) && { lfacDate: changesLigne.lfacDate }),
              ...((!ligne?.lfacLibelle && changesLigne?.lfacLibelle) && { lfacLibelle: changesLigne.lfacLibelle }),
              ...((!ligne?.dateDebutCharge && changesLigne?.dateDebutCharge)
                && { dateDebutCharge: changesLigne.dateDebutCharge }),
              ...((!ligne?.dateFinCharge && changesLigne?.dateFinCharge)
                && { dateFinCharge: changesLigne.dateFinCharge }),
            })),
          }
        ),
      });
    }
  }, [
    handleAsapCodesServiceOptionsSelect,
    handleCodeProduitLocalSelect,
    isTiersMulti,
    loadedPanelCodes,
    onChange,
    piece,
    selectedProfilEcriture,
  ]);

  /** Fonction appellé lors d'un changement dans un panel */
  const handlePanelChanges = React.useCallback((changes: any, panel: MouvementPanel) => {
    if (panel.isOnlyMouvement) {
      handleChangesMouvement(changes);
    } else {
      handleChangeLignes(changes);
      handleDefaultAttributes(pickBy(changes, (value) => value !== MULTI));
    }
  }, [handleChangeLignes, handleChangesMouvement]);

  const complements = React.useMemo(() => (
    <Fiche.Panel key="complements" id="complements" title="COMPLEMENTS">
      <FichePanelComplements
        codeProfilEcriture={codeProfilEcriture}
        data={{
          ...piece,
          ...panelData,
          ...panelOptions,
          isTiersMulti,
        }}
        getPanelDataOptions={handleLoadData}
        handleProfilEcritureChange={setSelectedProfilEcriture}
        identifiantEntite={piece?.exercice?.entite?.identifiantEntite}
        identifiantFiche={identifiant}
        isSearchable
        onChange={handlePanelChanges}
        panelType={PANEL_TYPE.MOUVEMENT}
        popupPosition="bottom right"
        profilEcriture={selectedProfilEcriture}
        sens={sens}
        setPanelOptions={setPanelOptions}
      />
    </Fiche.Panel>
  ), [
    codeProfilEcriture,
    handleLoadData,
    handlePanelChanges,
    identifiant,
    isTiersMulti,
    panelData,
    panelOptions,
    piece,
    selectedProfilEcriture,
    sens,
    setSelectedProfilEcriture,
  ]);

  const lignesMouvement = React.useMemo(() => (
    <Fiche.Panel key="lignesMouvement" id="lignesMouvement" title="LIGNES DE MOUVEMENT">
      <LignesPieces
        codeBHM={piece?.codeBHM}
        defaultAttributes={defaultAttributes}
        deletedLignes={deletedLignes}
        exercice={piece?.exercice}
        identifiantEntite={piece?.exercice?.entite?.identifiantEntite}
        lignes={lignes}
        onChange={handleChangesMouvement}
        panelOptions={panelOptions}
        profilEcriture={selectedProfilEcriture}
        sens={sens}
        setPanelOptions={setPanelOptions}
        tiers={selectedTiers}
      />
    </Fiche.Panel>
  ), [
    defaultAttributes,
    deletedLignes,
    handleChangesMouvement,
    lignes,
    panelOptions,
    piece?.codeBHM,
    piece?.exercice,
    selectedProfilEcriture,
    selectedTiers,
    sens,
  ]);

  const handleChangePj = React.useCallback((changes) => {
    onChange(changes);
  }, [onChange]);

  const handleDeletePj = React.useCallback((changes) => {
    onChange({
      deletedDocuments: [
        ...(piece?.deletedDocuments || []),
        changes,
      ],
    });
  }, [onChange, piece?.deletedDocuments]);

  const getPjSideBar = React.useMemo(() => (
    <PieceJointeBar
      color="purple"
      identifiantFichierLie={identifiant}
      nomFichierLie={Fichiers.MOUVEMENT}
      onChange={handleChangePj}
      onDeletePj={handleDeletePj}
      types={selectedProfilEcriture?.getNaturesGabarit(gabaritsPj)}
      readonly={readonly}
    />
  ), [
    handleDeletePj,
    handleChangePj,
    identifiant,
    gabaritsPj,
    selectedProfilEcriture,
    readonly,
  ]);

  const renderHeader = () => (
    <>
      {sens === Sens.DEPENSE ? 'MANDAT' : 'TITRE'}
      {' '}
      {piece?.numero}
      {' - '}
      {piece?.montantTtc}
      € TTC (COMPTABILISE
      {' '}
      <Icon name="money" />
      {' : '}
      {piece?.montantHt}
      €)
    </>
  );

  return (
    <span className="ficheWithSideBar">
      <Fiche
        header={renderHeader()}
        key="MouvementFiche"
        sidebars={{
          right: {
            alwaysPinned: true,
            content: getPjSideBar,
            icon: 'attach',
            width: '15vw',
          },
        }}
        type="piece"
        readonly={readonly}
        {...rest}
      >
        {informationsGenerales}
        {tiersPanel}
        {complements}
        {lignesMouvement}
        {isModalSemaphoreVisible && (
          <SemaphoreModal
            onChangeVisible={handleChangeModalSemaphoreVisible}
          />
        )}
      </Fiche>
    </span>
  );
};

export default MouvementFicheConsumer;
